// MentionTextarea — the shared Textarea wrapped in a Dropdown: typing `#`
// opens a variable menu under the field; picking an option (click or
// arrows + Enter) inserts a mention. Two DATA layers, one textarea:
//
//   stored  (the `value` prop / `onChangeValue` / the wire)  `… #[User Name]# …`
//   display (what the textarea shows)                        `… #User Name …`
//
// The transform runs at the prop boundary, so callers only ever see the
// stored form. The `#` sigil stays in the display form on purpose — encoding
// only tokenizes `#` + a KNOWN display (options ∪ existing map keys), so
// plain typed text never becomes a token while a hand-typed `#OTP` does.
// Deleting a character inside a token's display text is the intended
// degradation path: it stops matching, passes through as plain text, and its
// map entry drops. The mention map (display → machine value) is DERIVED from
// the stored text on every change; a token whose display isn't in `options`
// (e.g. inserted under another locale) keeps its previous mapping. Built for
// otpMessageContent/Map; watermarkText/UsedFieldsMap is the second caller.
//
// Highlighting: a textarea can't style ranges, so a mirror BACKDROP renders
// the same display text behind the textarea — plain runs in --text, tokens in
// --brand — while the textarea's own glyphs are transparent and contribute
// only the caret, selection, placeholder and border/focus states. The
// backdrop is therefore the ONLY paint of the text: its box metrics mirror
// .app-textarea's exactly (mention-textarea.scss) and it tracks the
// textarea's scroll.

import {
  forwardRef,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import Dropdown from './Dropdown';
import Icon from './Icon';
import Textarea from './Textarea';

export interface MentionOption {
  /** Machine value stored in the map (e.g. 'user-name'). */
  value: string;
  /** Display text — becomes the token text AND the map key, so it goes to the
   *  wire. Pass a stable literal, not a t() lookup: a translated display makes
   *  the stored value differ per authoring locale (see OTP_MENTIONS). */
  display: string;
  icon?: string;
}

export interface MentionTextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> {
  /** Stored form (`#[Display]#` tokens). */
  value: string;
  mentionMap: Record<string, string>;
  options: MentionOption[];
  /** Fires for every edit with the next stored value and the map rebuilt from it. */
  onChangeValue: (value: string, mentionMap: Record<string, string>) => void;
}

const TOKEN_RE = /#\[([^\]]+)\]#/g;

/** Rebuild the map from the tokens actually present in the stored text. */
const deriveMap = (
  stored: string,
  options: MentionOption[],
  prev: Record<string, string>,
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const m of stored.matchAll(TOKEN_RE)) {
    const display = m[1];
    const value = options.find((o) => o.display === display)?.value ?? prev[display];
    if (value !== undefined) map[display] = value;
  }
  return map;
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** stored ⇄ display transforms, restricted to the known displays so an
 *  unknown literal `#[X]#` stays verbatim and text never changes under the
 *  user (decode(encode(t)) is stable). */
const buildTransforms = (known: string[]) => {
  if (known.length === 0) {
    return { decode: (t: string) => t, encode: (t: string) => t };
  }
  const alt = known.map(escapeRe).join('|');
  const decodeRe = new RegExp(`#\\[(${alt})\\]#`, 'g');
  const encodeRe = new RegExp(`#(${alt})`, 'g');
  return {
    decode: (t: string) => t.replace(decodeRe, '#$1'),
    encode: (t: string) => t.replace(encodeRe, '#[$1]#'),
  };
};

/** Split the display text into plain runs and `#Display` token runs — the
 *  same alternation the encoder uses, so exactly the substrings that would
 *  round-trip as tokens get pills (a hand-broken token loses its pill the
 *  moment it stops matching, the existing degradation path). */
const segmentDisplay = (text: string, known: string[]): { text: string; isToken: boolean }[] => {
  if (known.length === 0 || !text) return [{ text, isToken: false }];
  const re = new RegExp(`#(${known.map(escapeRe).join('|')})`, 'g');
  const out: { text: string; isToken: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: text.slice(last, i), isToken: false });
    out.push({ text: m[0], isToken: true });
    last = i + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), isToken: false });
  return out;
};

const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  function MentionTextarea(
    { value, mentionMap, options, onChangeValue, onKeyDown, onScroll, ...rest },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const setRefs = (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const backdropRef = useRef<HTMLSpanElement | null>(null);
    // The backdrop scrolls with the textarea, or pills detach from their text.
    const syncScroll = () => {
      const el = innerRef.current;
      const bd = backdropRef.current;
      if (el && bd) bd.scrollTop = el.scrollTop;
    };

    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    /** Display-space index of the `#` that opened the panel — replaced on insert. */
    const hashPos = useRef<number | null>(null);

    // Longest-first so a display that prefixes another can't shadow it.
    const knownDisplays = Array.from(
      new Set([...options.map((o) => o.display), ...Object.keys(mentionMap)]),
    ).sort((a, b) => b.length - a.length);
    const { decode, encode } = buildTransforms(knownDisplays);
    const displayText = decode(value);

    const emit = (nextDisplay: string) => {
      const stored = encode(nextDisplay);
      onChangeValue(stored, deriveMap(stored, options, mentionMap));
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      const caret = e.target.selectionStart ?? 0;
      // A single typed `#` opens the menu (pastes containing `#` don't) —
      // compared in display space, the space the textarea actually edits.
      if (options.length > 0 && next.length === displayText.length + 1 && next[caret - 1] === '#') {
        hashPos.current = caret - 1;
        setActive(0);
        setOpen(true);
      } else if (open) {
        setOpen(false);
      }
      emit(next);
      requestAnimationFrame(syncScroll);
    };

    const insert = (option: MentionOption) => {
      const pos = hashPos.current;
      if (pos === null) return;
      const el = innerRef.current;
      const mention = `#${option.display}`;
      setOpen(false);
      hashPos.current = null;
      emit(displayText.slice(0, pos) + mention + displayText.slice(pos + 1));
      // Refocus with the caret after the mention once the new value renders.
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(pos + mention.length, pos + mention.length);
      });
    };

    // Arrow/Enter drive the menu without focus ever leaving the field
    // (Escape + outside click are handled by Dropdown).
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (open) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const dir = e.key === 'ArrowDown' ? 1 : -1;
          setActive((i) => (i + dir + options.length) % options.length);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          insert(options[active]);
          return;
        }
      }
      onKeyDown?.(e);
    };

    return (
      <Dropdown
        className='app-mention'
        open={open}
        onClose={() => setOpen(false)}
        align='start'
        trigger={
          <span className='app-mention__editor'>
            <span ref={backdropRef} className='app-mention__backdrop' aria-hidden='true'>
              {segmentDisplay(displayText, knownDisplays).map((seg, i) =>
                seg.isToken ? (
                  <span key={i} className='app-mention__token'>
                    {seg.text}
                  </span>
                ) : (
                  seg.text
                ),
              )}
              {'\n'}
            </span>
            <Textarea
              ref={setRefs}
              value={displayText}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onScroll={(e) => {
                syncScroll();
                onScroll?.(e);
              }}
              {...rest}
            />
          </span>
        }
      >
        {options.map((option, i) => (
          <button
            key={option.value}
            type='button'
            className={`app-dropdown__item${i === active ? ' is-active' : ''}`}
            onClick={() => insert(option)}
            onMouseEnter={() => setActive(i)}
          >
            {option.icon && <Icon name={option.icon} size={16} />}
            <span className='app-dropdown__item-label'>{option.display}</span>
          </button>
        ))}
      </Dropdown>
    );
  },
);

export default MentionTextarea;
